import styled from 'styled-components';
import { FormGroup, Input, Select } from '@snsw/react-component-library';
import { FlexBox } from 'components/Layout';
import { ChangeEventHandler, ForwardedRef, forwardRef, useLayoutEffect, useMemo, useRef } from 'react';
import { useOptionallyControlledValue } from 'components/Utils/useOptionallyControlledValue';
import { OverseasNumberModel } from './type';
import countryCodes from 'country-codes-list';

const UnspacedFormGroup = styled(FormGroup)`
  margin-top: 0;

  span[class^='FormError'] {
    padding-top: 2px;
  }
`;

interface KiamaSelectOption {
  text: string;
  value: string;
}

export type OverseasContactNumberProps = {
  /**
   * The id to be used for each of the form groups. This will be applied to the number, suffixed with `-dialing-code`
   * on the select component.
   */
  id?: string;

  /**
   * The name to be used for the number form control. This will be suffixed with `-dialing-code` on the select
   * component.
   */
  name: string;

  /** Label to show above the field */
  label?: string;

  /** Optional help message to show below the label */
  helpMessage?: string;

  /** Error message for each of the form components */
  errorMessage?: string;

  /** Determines if the relevant `errorMessage` will be shown next to each field */
  hasError?: boolean;

  disabled?: boolean;

  /** Should non-numeric characters be allowed in the 'number' field? Defaults to `false`. */
  allowNonNumeric?: boolean;

  /** Default value to supply for an uncontrolled input */
  defaultValue?: OverseasNumberModel;

  /** Actual value for controlled input */
  value?: OverseasNumberModel;

  /** Optional filter that can be applied to remove certain countries from the list */
  filter?: (countryName: string) => boolean;

  /** Countries to add to the top of the list */
  priorityCountryNames?: string[];

  /** Country Code placeholder  */
  codePlaceHolder?: string;

  /** Notified when the prefix or the number changes */
  onChange?: (value: OverseasNumberModel) => void;
};

const getInitialValue = (...initialValues: OverseasNumberModel[]) =>
  initialValues.find((v) => Boolean(v?.code || v?.number)) || {
    code: '',
    number: '',
  };

const toOption = (countryData): KiamaSelectOption => ({
  text: `${countryData.countryNameEn} +${countryData.countryCallingCode}`,
  value: countryData.countryCallingCode,
});

const useCountries = (
  filter: OverseasContactNumberProps['filter'],
  priorityCountryNames: OverseasContactNumberProps['priorityCountryNames']
): KiamaSelectOption[] => {
  return useMemo(() => {
    const countries = filter != null ? countryCodes.all().filter((c) => filter(c.countryNameEn)) : countryCodes.all();

    const countryOptions = countries.map(toOption).sort((left, right) => left.text.localeCompare(right.text));

    if (!(priorityCountryNames?.length > 0)) return countryOptions;

    const priorityCountryOptions = countries
      .filter((c) => priorityCountryNames.includes(c.countryNameEn))
      .map(toOption);

    return priorityCountryOptions.length > 0
      ? [...priorityCountryOptions, { text: '-----', value: '' }, ...countryOptions]
      : countryOptions;
  }, [filter, priorityCountryNames]);
};

const OverseasContactNumberField = forwardRef(
  (
    {
      id,
      name,
      allowNonNumeric = false,
      hasError,
      disabled,
      label,
      filter,
      priorityCountryNames,
      defaultValue = {},
      value: controlledValue,
      onChange,
    }: OverseasContactNumberProps,
    ref?: ForwardedRef<HTMLInputElement>
  ) => {
    const selectRef = useRef<HTMLSelectElement>();

    const options = useCountries(filter, priorityCountryNames);

    const [value, handleValueChange] = useOptionallyControlledValue<OverseasNumberModel | undefined>(
      () => getInitialValue(defaultValue, controlledValue),
      controlledValue,
      onChange
    );

    const handleSelectCodeChange: ChangeEventHandler<HTMLSelectElement> = (event) => {
      const { value: code } = event.target;

      // Don't allow selecting the divider when there is a priority country list
      if (code === '') return;

      handleValueChange({ ...value, code });
    };

    const handleNumberChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      const next = event.target.value;
      const isValid = allowNonNumeric || !/\D/.test(next);

      if (isValid) {
        handleValueChange({ ...value, number: next });
      }
    };

    // Poke a hidden <Option /> element above the first one to represent the selected value.
    useLayoutEffect(() => {
      const select = selectRef.current;

      let selected = select.options.item(0);
      if (!selected.hidden) {
        selected = document.createElement('option');
        selected.hidden = selected.selected = true;
        select.options.add(selected, 0);
      }

      selected.value = value?.code ?? '';
      selected.text = Boolean(value?.code) ? `+${value.code}` : '';
    }, [value.code]);

    const selectId = Boolean(id) ? `${id}-dialing-code` : undefined;
    const selectName = `${name}-dialing-code`;

    return (
      <FlexBox direction="row" gap={1}>
        <Select
          inputRef={selectRef}
          id={selectId}
          name={selectName}
          aria-label={`${label} dialing prefix`}
          placeholder=""
          options={options}
          hasError={hasError}
          onChange={handleSelectCodeChange}
          value={value?.code}
          disabled={disabled}
          inputWidth="xs"
        />

        <Input
          inputRef={ref}
          id={id}
          name={name}
          hasError={hasError}
          onChange={handleNumberChange}
          value={value?.number ?? ''}
          disabled={disabled}
          inputWidth="lg"
        />
      </FlexBox>
    );
  }
);

export const OverseasContactNumber = forwardRef<HTMLInputElement, OverseasContactNumberProps>((props, ref) => {
  return (
    <UnspacedFormGroup
      id={props.id ?? props.name}
      label={props.label}
      helpMessage={props.helpMessage ?? 'For overseas numbers, and include all area codes.'}
      hasError={props.hasError}
      errorMessage={props.errorMessage}
    >
      <OverseasContactNumberField ref={ref} {...props} />
    </UnspacedFormGroup>
  );
});
